import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "../api";
import { getPasswordSelector } from "./authSlice";
import { cloneDeep } from "lodash";

// ACTIONS --------------------------------------------------------------------

/**
 * @desc Get live conventions.
 */
export const fetchLiveConventions = createAsyncThunk(
  "liveConventions/fetch",
  async (_data, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;
      const token = getPasswordSelector(state);
      const response = await API.LiveConvention.GetAll({ token });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Update live convention.
 */
export const updateLiveConvention = createAsyncThunk(
  "liveConvention/update",
  async (data, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;
      const token = getPasswordSelector(state);
      const response = await API.LiveConvention.Update({
        params: {
          cname: data.cname,
          brand: data.brand,
        },
        token,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Delete live convention.
 */
export const deleteLiveConvention = createAsyncThunk(
  "liveConvention/delete",
  async (data, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;
      const token = getPasswordSelector(state);
      const response = await API.LiveConvention.Delete({
        params: {
          brand: data.brand,
        },
        token,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// SLICE ----------------------------------------------------------------------

/**
 * @desc Live Conventions Slice.
 */
export const liveConventionsSlice = createSlice({
  name: "liveConventions",
  initialState: {
    pending: false,
    data: [],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Live Conventions
      .addCase(fetchLiveConventions.pending, (state) => {
        state.pending = true;
      })
      .addCase(fetchLiveConventions.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
        }
        state.pending = false;
      })
      .addCase(fetchLiveConventions.rejected, (state) => {
        state.pending = false;
      })

      // Update Live Convention
      .addCase(updateLiveConvention.pending, (state) => {
        state.pending = true;
      })
      .addCase(updateLiveConvention.fulfilled, (state, action) => {
        const newConvention = action.payload;
        const copy = cloneDeep(state.data).filter(
          (liveConventions) => liveConventions.brand !== newConvention.brand
        );
        copy.push(newConvention);
        state.data = copy;
        state.pending = false;
      })
      .addCase(updateLiveConvention.rejected, (state) => {
        state.pending = false;
      })

      // Delete Live Convention
      .addCase(deleteLiveConvention.pending, (state) => {
        state.pending = true;
      })
      .addCase(deleteLiveConvention.fulfilled, (state, action) => {
        const deletedBrand = action.meta.arg.brand;
        const copy = cloneDeep(state.data).filter(
          (liveConventions) => liveConventions.brand !== deletedBrand
        );
        state.data = copy;
        state.pending = false;
      })
      .addCase(deleteLiveConvention.rejected, (state) => {
        state.pending = false;
      });
  },
});

// SELECTORS ------------------------------------------------------------------

export const getLiveConventionsSelector = (state) => state.liveConventions.data;

// REDUCER --------------------------------------------------------------------

export default liveConventionsSlice.reducer;
